import React, { useEffect, useRef, useState } from 'react';

interface FeedItem {
  text: string;
  sentiment?: string;
  url?: string;
  captured_at?: string;
  source?: string;
}

interface TelegramMessage {
  message?: string;
  text?: string;
  timestamp?: string;
  channel?: string;
}

interface Props {
  twitterFeeds: FeedItem[];
  newsFeeds: FeedItem[];
  keyThemes?: string[];
}

const sentColor: Record<string, string> = {
  positive: '#26a65b',
  bullish: '#26a65b',
  negative: '#e74c3c',
  bearish: '#e74c3c',
  neutral: '#f39c12',
};

function SentimentDot({ sentiment }: { sentiment?: string }) {
  const color = sentColor[sentiment?.toLowerCase() || ''] || '#f39c12';
  return <span style={{ color, fontSize: '0.6rem', marginRight: 4 }}>●</span>;
}

function FeedList({ items, emptyMsg }: { items: FeedItem[]; emptyMsg: string }) {
  if (!items.length) {
    return <div style={{ color: '#444', fontSize: '0.7rem', padding: '1rem 0', textAlign: 'center' }}>{emptyMsg}</div>;
  }
  return (
    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <SentimentDot sentiment={item.sentiment} />
            <div style={{ color: '#ccc', fontSize: '0.7rem', lineHeight: 1.4, flex: 1 }}>
              {item.text}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
            {item.url ? (
              <a href={item.url} target="_blank" rel="noreferrer" style={{ color: '#3498db', fontSize: '0.58rem' }}>
                View source ↗
              </a>
            ) : <span />}
            <span style={{ color: '#444', fontSize: '0.58rem' }}>
              {item.captured_at ? new Date(item.captured_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LiveFeeds({ twitterFeeds, newsFeeds, keyThemes = [] }: Props) {
  const [activeTab, setActiveTab] = useState<'twitter' | 'news'>('twitter');

  const tabs = [
    { key: 'twitter' as const, label: `X / Twitter (${twitterFeeds.length})`, color: '#1da1f2' },
    { key: 'news' as const, label: `News (${newsFeeds.length})`, color: '#9b59b6' },
  ];

  return (
    <div style={{ background: '#0d0d14', border: '1px solid #222', borderRadius: 8 }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1a1a1a' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            flex: 1, padding: '8px 0', background: 'transparent',
            border: 'none', cursor: 'pointer',
            borderBottom: activeTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
            color: activeTab === tab.key ? tab.color : '#555',
            fontSize: '0.68rem', fontWeight: 600,
            transition: 'color 0.2s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Key themes */}
      {keyThemes.length > 0 && (
        <div style={{ padding: '6px 10px', borderBottom: '1px solid #1a1a1a', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {keyThemes.slice(0, 5).map((theme, i) => (
            <span key={i} style={{
              background: '#1a1a2e', color: '#7986cb', fontSize: '0.6rem',
              padding: '2px 7px', borderRadius: 10,
            }}>
              {theme}
            </span>
          ))}
        </div>
      )}

      <div style={{ padding: '0 10px 8px' }}>
        {activeTab === 'twitter' && (
          <FeedList items={twitterFeeds} emptyMsg="No Twitter data. Grok sentiment cron will populate this." />
        )}
        {activeTab === 'news' && (
          <FeedList items={newsFeeds} emptyMsg="No news available." />
        )}
      </div>
    </div>
  );
}
